import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { InvoiceState, InvoiceStateLabel } from '../Invoices.const';
import { invoicesSlice, selectinvoicesData } from '../../../redux/invoices';

const StyledDrawer = styled(Drawer)`
  min-width: 388px;
  max-width: 400px;
  border-radius: 0px;

  .MuiDrawer-paper {
    min-width: 388px;
    max-width: 400px;
    border-radius: 0px;
  }
`;

export const InvoicesFilter = () => {
  const dispatch = useAppDispatch();
  const invoicesPageData = useAppSelector(selectinvoicesData);

  const {
    areFiltersApplied,
    isFilterOpen,
    filterBy: { invoiceState, projectNames, showZeroInvoices },
    invoices: { data },
  } = invoicesPageData;

  const invoicesStatesLists = useMemo(() => {
    return Object.values(InvoiceState);
  }, []);

  const uniqueProjectNames = useMemo(() => {
    if (!data) return [];

    const projectNamesSet = new Set<string>();
    data.forEach((invoice) => {
      if (invoice?.projectName) projectNamesSet.add(invoice.projectName);
    });

    return Array.from(projectNamesSet);
  }, [data]);

  const handleFilterInvoicesByStatus = useCallback(
    (e: SelectChangeEvent<InvoiceState>) => {
      dispatch(
        invoicesSlice.actions.setFilterByInvoiceState(
          e.target.value as InvoiceState,
        ),
      );
    },
    [dispatch],
  );

  const onChangeFilterValue = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value as string;
      dispatch(invoicesSlice.actions.setFilterByProjectName(value));
    },
    [dispatch],
  );

  const onDeleteFilterValue = useCallback(
    (value: string) => {
      dispatch(invoicesSlice.actions.removeProjectFromFilter(value));
    },
    [dispatch],
  );

  const resetFilters = useCallback(() => {
    dispatch(invoicesSlice.actions.resetFilters(InvoiceState.ALL));
  }, [dispatch]);

  const closeFilter = useCallback(() => {
    dispatch(invoicesSlice.actions.closeFilter());
  }, [dispatch]);

  if (!data) {
    return <></>;
  }

  return (
    <StyledDrawer
      anchor="right"
      open={isFilterOpen}
      keepMounted
      onClose={closeFilter}
    >
      <DialogTitle style={{ display: 'flex', gap: 12 }}>
        Filters{' '}
        {areFiltersApplied && (
          <Button
            variant="text"
            qa-id="dropdown-menu-button"
            onClick={resetFilters}
            sx={{
              backgroundColor: '#9c27b020',
            }}
            color="secondary"
          >
            <RemoveCircleOutlineIcon />
            &nbsp;Reset all
          </Button>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
        onClick={closeFilter}
      >
        <CloseIcon />
      </IconButton>
      <Grid container padding={2}>
        <Grid item xs={12} marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id="filterValuelabel">Select project name</InputLabel>
            <Select
              label="Select project value"
              labelId="filterValuelabel"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    return (
                      <Chip
                        key={value}
                        onDelete={() => {
                          onDeleteFilterValue(value);
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        label={value}
                      />
                    );
                  })}
                </Box>
              )}
              value={projectNames}
              onChange={onChangeFilterValue}
            >
              {!uniqueProjectNames.length && (
                <MenuItem disabled>No options available.</MenuItem>
              )}

              {uniqueProjectNames.map((name: string) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Status filtering */}
        <Grid item xs={12} marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id="Status">Status</InputLabel>
            <Select
              labelId="Status"
              value={invoiceState}
              label="Status"
              onChange={handleFilterInvoicesByStatus}
            >
              {invoicesStatesLists.map((item) => (
                <MenuItem key={item} value={item}>
                  {InvoiceStateLabel[item]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} marginTop={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showZeroInvoices}
                onChange={(e) =>
                  dispatch(
                    invoicesSlice.actions.setShowZeroInvoices(e.target.checked),
                  )
                }
                color="secondary"
              />
            }
            label={<Typography variant="body2">Show $0 invoices</Typography>}
          />
        </Grid>
      </Grid>
    </StyledDrawer>
  );
};
